import React, { Component } from 'react';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { AlertCircle } from 'lucide-react';
import * as Sentry from '@sentry/react';
import { isChunkLoadError } from '@/lib/chunk-error-handler';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  showDialog?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isReloading: boolean;
}

const CHUNK_RELOAD_KEY = 'dnceclub_chunk_reload_boundary';

/**
 * Check if we've already tried reloading recently to prevent infinite loops
 */
function hasReloadedRecently(): boolean {
  const lastReload = sessionStorage.getItem(CHUNK_RELOAD_KEY);
  if (!lastReload) return false;

  // Consider reload "recent" if it was within the last 10 seconds
  const timeSinceReload = Date.now() - parseInt(lastReload, 10);
  return timeSinceReload < 10000;
}

/**
 * Mark that we've attempted a reload
 */
function markReloadAttempt(): void {
  sessionStorage.setItem(CHUNK_RELOAD_KEY, Date.now().toString());
}

/**
 * Clear caches and reload with a cache-busting query param. The query param
 * ensures the browser + CDN can't serve a stale HTML document that still
 * references chunks removed by a new deploy.
 */
function clearCachesAndReload(): void {
  const doReload = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('_v', Date.now().toString());
    window.location.replace(url.toString());
  };

  if ('caches' in window && window.caches) {
    window.caches.keys().then(names => {
      Promise.all(names.map(name => window.caches.delete(name))).then(doReload);
    }).catch(doReload);
  } else {
    doReload();
  }
}

export class SentryErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    isReloading: false
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Check if this is a chunk load error and we haven't reloaded recently
    if (isChunkLoadError(error) && !hasReloadedRecently()) {
      console.warn('Chunk load error detected in error boundary, auto-reloading...');
      this.setState({ isReloading: true });
      markReloadAttempt();

      // Clear caches and reload
      clearCachesAndReload();
      return;
    }

    console.error('Error caught by boundary:', error, errorInfo);

    // Send error to Sentry (but not chunk errors, those are handled)
    if (!isChunkLoadError(error)) {
      Sentry.withScope((scope) => {
        scope.setContext('errorBoundary', {
          componentStack: errorInfo.componentStack,
        });
        Sentry.captureException(error);
      });
    }
  }

  public render() {
    // Show normal loading state while auto-reloading for chunk errors
    // Users just see "Loading..." like any other lazy load
    if (this.state.isReloading) {
      return (
        <div className="fixed inset-0 bg-transparent flex flex-col items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      );
    }

    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Check if this was a chunk error that we already tried to auto-reload
      const isChunkError = this.state.error && isChunkLoadError(this.state.error);

      return (
        <div className="flex flex-col items-center justify-center min-h-[400px] p-8">
          <AlertCircle className="h-12 w-12 text-destructive mb-4" />
          <h2 className="text-xl font-semibold mb-2 text-on-light">
            {isChunkError ? 'Update required' : 'Something went wrong'}
          </h2>
          <p className="text-sm text-on-light-muted mb-4 text-center max-w-md">
            {isChunkError
              ? 'A new version of the app is available. Please reload to continue.'
              : (this.state.error?.message || 'An unexpected error occurred')
            }
          </p>
          <div className="flex gap-2">
            <Button
              onClick={() => {
                // Clear the reload attempt marker so manual reload works
                sessionStorage.removeItem(CHUNK_RELOAD_KEY);
                this.setState({ hasError: false, error: null, isReloading: false });
                // Clear caches before reload
                clearCachesAndReload();
              }}
              variant="outline"
              className="text-on-light border-on-light border-2 hover:text-on-light-muted hover:bg-on-dark"
            >
              RELOAD PAGE
            </Button>
            {this.props.showDialog && !isChunkError && (
              <Button
                onClick={() => {
                  Sentry.showReportDialog({
                    eventId: Sentry.lastEventId(),
                  });
                }}
                className="bg-on-light text-on-dark hover:bg-on-light/80"
              >
                REPORT ISSUE
              </Button>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Export the Sentry ErrorBoundary HOC for functional component usage
export const withSentryErrorBoundary = Sentry.withErrorBoundary;